/**
 * LargeFileHelper Component
 * Guides users through extracting audio from large video files using FFmpeg
 */

import React, { useState, useCallback } from 'react';

interface LargeFileHelperProps {
  file: File;
  onClose: () => void;
  onFileReady?: (message: string) => void;
}

type Step = 'intro' | 'install' | 'extract' | 'verify';

export const LargeFileHelper: React.FC<LargeFileHelperProps> = ({ file, onClose, onFileReady }) => {
  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [copied, setCopied] = useState<string | null>(null);

  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
  const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  const outputFileName = `${fileNameWithoutExt}.mp3`;

  const commands = {
    install: 'winget install FFmpeg',
    extract: `ffmpeg -i "${file.name}" -vn -acodec libmp3lame -q:a 2 "${outputFileName}"`,
    checkInstall: 'ffmpeg -version',
    splitCommand: `ffmpeg -i "${outputFileName}" -f segment -segment_time 1800 -c copy "${fileNameWithoutExt}_part%03d.mp3"`,
  };

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const CopyButton: React.FC<{ text: string; label: string; small?: boolean }> = ({ text, label, small }) => (
    <button
      onClick={() => copyToClipboard(text, label)}
      className={`absolute ${small ? 'top-1 right-1 p-1.5' : 'top-2 right-2 p-2'} bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors`}
    >
      {copied === label ? (
        <svg className={`${small ? 'w-3 h-3' : 'w-4 h-4'} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className={`${small ? 'w-3 h-3' : 'w-4 h-4'} text-slate-300`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'intro':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-black text-amber-900 uppercase tracking-wider text-sm">File Too Large</h4>
                  <p className="text-amber-700 text-sm mt-1">
                    <span className="font-bold">{file.name}</span> is <span className="font-bold">{fileSizeMB} MB</span>. Upload limit is 100 MB.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-indigo-50 border-2 border-indigo-200 rounded-2xl">
              <h4 className="font-black text-indigo-900 uppercase tracking-wider text-sm mb-3">The Solution</h4>
              <p className="text-indigo-700 text-sm leading-relaxed">
                Extract just the audio track. This typically reduces file size by <span className="font-bold">10-15x</span>.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-2xl font-black text-slate-400">{fileSizeMB} MB</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Video File</p>
                </div>
                <div className="p-3 bg-white rounded-xl">
                  <p className="text-2xl font-black text-indigo-600">~{Math.round(Number(fileSizeMB) / 12)} MB</p>
                  <p className="text-[10px] text-indigo-500 uppercase tracking-wider">Audio Only</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600 text-center">
              This wizard guides you through extracting audio using <span className="font-bold">FFmpeg</span> (free, open-source).
            </p>
          </div>
        );

      case 'install':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-slate-50 border-2 border-slate-200 rounded-2xl">
              <h4 className="font-black text-slate-900 uppercase tracking-wider text-sm mb-4">Step 1: Install FFmpeg</h4>
              <p className="text-sm text-slate-600 mb-4">Open <span className="font-bold">PowerShell</span> and run:</p>
              <div className="relative">
                <pre className="bg-slate-900 text-emerald-400 p-4 rounded-xl text-sm font-mono overflow-x-auto">{commands.install}</pre>
                <CopyButton text={commands.install} label="install" />
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Or download from <a href="https://ffmpeg.org/download.html" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">ffmpeg.org</a>
              </p>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-sm text-emerald-700"><span className="font-bold">Already have FFmpeg?</span> Skip to next step.</p>
            </div>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <p className="text-xs text-slate-600 mb-2">Verify installation:</p>
              <div className="relative">
                <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg text-xs font-mono">{commands.checkInstall}</pre>
                <CopyButton text={commands.checkInstall} label="check" small />
              </div>
            </div>
          </div>
        );

      case 'extract':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-slate-50 border-2 border-slate-200 rounded-2xl">
              <h4 className="font-black text-slate-900 uppercase tracking-wider text-sm mb-4">Step 2: Extract Audio</h4>
              <p className="text-sm text-slate-600 mb-4">Navigate to your video folder in PowerShell, then run:</p>
              <div className="relative">
                <pre className="bg-slate-900 text-emerald-400 p-4 rounded-xl text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all">{commands.extract}</pre>
                <CopyButton text={commands.extract} label="extract" />
              </div>
              <div className="mt-4 p-3 bg-white border border-slate-200 rounded-xl">
                <p className="text-xs text-slate-500 mb-1">Output file:</p>
                <p className="text-sm font-bold text-indigo-600 font-mono">{outputFileName}</p>
              </div>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700"><span className="font-bold">Processing time:</span> 1-hour video takes ~1-3 minutes.</p>
            </div>
            <details className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <summary className="text-sm font-bold text-slate-700 cursor-pointer">Still too large? Split into parts</summary>
              <div className="mt-4">
                <p className="text-xs text-slate-600 mb-3">Split into 30-minute chunks:</p>
                <div className="relative">
                  <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">{commands.splitCommand}</pre>
                  <CopyButton text={commands.splitCommand} label="split" small />
                </div>
              </div>
            </details>
          </div>
        );

      case 'verify':
        return (
          <div className="space-y-6">
            <div className="p-6 bg-slate-50 border-2 border-slate-200 rounded-2xl">
              <h4 className="font-black text-slate-900 uppercase tracking-wider text-sm mb-4">Step 3: Upload</h4>
              <div className="space-y-4">
                {['Check that the MP3 file was created', 'Verify file size is under 100 MB', 'Close this dialog and upload the MP3'].map((text, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-black text-indigo-600">{idx + 1}</span>
                    </div>
                    <p className="text-sm text-slate-700">{text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <p className="text-sm text-emerald-700"><span className="font-bold">Pro tip:</span> Audio quality is identical for transcription purposes.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'intro', label: 'Overview' },
    { key: 'install', label: 'Install' },
    { key: 'extract', label: 'Extract' },
    { key: 'verify', label: 'Upload' },
  ];

  const currentIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-[32px] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-wider">Large File Assistant</h3>
            <p className="text-xs text-slate-500 mt-1">Extract audio for transcription</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-8 py-4 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.key}>
                <button
                  onClick={() => setCurrentStep(step.key)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                    currentStep === step.key ? 'bg-indigo-100 text-indigo-700' : idx < currentIndex ? 'text-emerald-600' : 'text-slate-400'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                    currentStep === step.key ? 'bg-indigo-600 text-white' : idx < currentIndex ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {idx < currentIndex ? '✓' : idx + 1}
                  </div>
                  <span className="text-xs font-bold hidden sm:inline">{step.label}</span>
                </button>
                {idx < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${idx < currentIndex ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-8">{renderStep()}</div>
        <div className="px-8 py-6 border-t border-slate-100 flex justify-between">
          <button
            onClick={() => currentIndex > 0 && setCurrentStep(steps[currentIndex - 1].key)}
            disabled={currentIndex === 0}
            className="px-6 py-3 text-slate-600 font-bold rounded-xl hover:bg-slate-100 transition-colors disabled:opacity-30"
          >
            Back
          </button>
          {currentIndex < steps.length - 1 ? (
            <button onClick={() => setCurrentStep(steps[currentIndex + 1].key)} className="px-8 py-3 bg-indigo-600 text-white font-black uppercase tracking-wider rounded-xl hover:bg-indigo-700 transition-colors">
              Next Step
            </button>
          ) : (
            <button onClick={() => { onFileReady?.(`Upload ${outputFileName}`); onClose(); }} className="px-8 py-3 bg-emerald-600 text-white font-black uppercase tracking-wider rounded-xl hover:bg-emerald-700 transition-colors">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};